import { useState, useEffect, useCallback } from 'react';
import { syncEngine, SyncEngineState } from '@/lib/offline/sync';
import { offlineDb } from '@/lib/offline/database';
import { useLocationStore } from '@/store/location';
import { useAuth } from './useAuth';

export interface OfflineState extends SyncEngineState {
  isInitialized: boolean;
}

export function useOffline() {
  const { profile } = useAuth();
  const { currentLocation } = useLocationStore();
  const [state, setState] = useState<OfflineState>({
    isOnline: navigator.onLine,
    isSyncing: false,
    pendingCount: 0,
    failedCount: 0,
    lastSyncAt: null,
    isInitialized: false,
  });

  useEffect(() => {
    if (!currentLocation?.id || !profile?.id) return;

    // Start sync engine
    syncEngine.start(currentLocation.id, profile.id).then(() => {
      setState((prev) => ({ ...prev, isInitialized: true }));
    });

    // Subscribe to state changes
    const unsubscribe = syncEngine.subscribe((newState) => {
      setState((prev) => ({ ...prev, ...newState }));
    });

    return () => {
      unsubscribe();
    };
  }, [currentLocation?.id, profile?.id]);

  const syncNow = useCallback(async () => {
    return syncEngine.syncNow();
  }, []);

  const retryFailed = useCallback(async () => {
    return syncEngine.retryFailed();
  }, []);

  const discardFailed = useCallback(async () => {
    return syncEngine.discardFailed();
  }, []);

  const testConnection = useCallback(async () => {
    return syncEngine.testConnection();
  }, []);

  const resetLocalData = useCallback(async () => {
    return syncEngine.resetLocalData();
  }, []);

  const getStorageUsage = useCallback(async () => {
    return offlineDb.getStorageUsage();
  }, []);

  const setActiveSession = useCallback((active: boolean) => {
    syncEngine.setActiveSession(active);
  }, []);

  return {
    ...state,
    syncNow,
    retryFailed,
    discardFailed,
    testConnection,
    resetLocalData,
    getStorageUsage,
    setActiveSession,
  };
}

// Format bytes to human readable
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// Format relative time
export function formatRelativeTime(date: Date | null): string {
  if (!date) return 'Never';

  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString();
}
